import MarkdownIt from 'markdown-it';
// @ts-expect-error markdown-it-mark не поставляет типы. ==текст== → <mark>.
import markdownItMark from 'markdown-it-mark';
import TurndownService from 'turndown';

/**
 * Конвертер markdown ↔ HTML для Tiptap-редактора.
 *
 * Tiptap работает с HTML внутри (ProseMirror schema), но мы храним
 * статьи как .md в git-репо vault'а — это формат source-of-truth.
 *
 * Поэтому при загрузке статьи в редактор: markdown → HTML (через
 * markdown-it). При сохранении: HTML → markdown (через turndown).
 *
 * Поддерживаем:
 *  - заголовки, параграфы, bold/italic/underline/strike/code
 *  - bullet/ordered/task lists (через GFM)
 *  - blockquote, code-block, hr, link, image
 *  - таблицы (через GFM)
 *  - [[wikilinks]] — наш обычай: при HTML→md оставляем как есть
 */

// markdown → HTML
const md = new MarkdownIt({
  html: false,
  linkify: true,
  breaks: false,
  typographer: true,
});
md.use(markdownItMark); // ==выделение== → <mark> (жёлтый highlight)
installCalloutRule(md);
installGalleryFence(md);
installFkTableFence(md);
installVideoFence(md);
installLayoutFences(md);
installMiscFences(md);
installFigureRule(md);
installWikilinkRule(md);
installEscapedFileLinkRule(md);
installTaskListRule(md);

export function markdownToHtml(mdSrc: string): string {
  return postProcessHtml(md.render(mdSrc));
}

/**
 * Пост-обработка HTML после markdown-it (синхронно с бэком):
 *  - <p><figure class="fk-figure">…</figure></p> → figure без абзаца
 *    (картинка — inline-токен, а figure блочный);
 *  - цитата с автором: последний абзац `— Автор` → <cite> и class="fk-quote".
 */
export function postProcessHtml(html: string): string {
  return html
    .replace(/<p>(<figure class="fk-figure"[\s\S]*?<\/figure>)<\/p>/g, '$1')
    .replace(
      /<blockquote>\n([\s\S]*?)<p>(?:—|–|--|-)\s+([^<]+)<\/p>\n<\/blockquote>/g,
      '<blockquote class="fk-quote">\n$1<cite>$2</cite>\n</blockquote>',
    );
}

// HTML → markdown
const turndown = new TurndownService({
  headingStyle: 'atx', // # для H1
  hr: '---',
  bulletListMarker: '-',
  codeBlockStyle: 'fenced', // ``` для блоков
  emDelimiter: '_',
  strongDelimiter: '**',
});

// Сохраняем [[wikilinks]] обратно в markdown
turndown.addRule('wikilink', {
  filter: (node) =>
    node.nodeName === 'A' && node.getAttribute('data-wikilink') !== null,
  replacement: (_content, node) => {
    const el = node as HTMLElement;
    const slug = el.getAttribute('data-wikilink') || '';
    const label = el.textContent || '';
    return label && label !== slug ? `[[${slug}|${label}]]` : `[[${slug}]]`;
  },
});

// Чек-листы (task list items) → `- [ ]` / `- [x]`
turndown.addRule('taskItem', {
  filter: (node) => {
    if (node.nodeName !== 'LI') return false;
    const cls = (node as HTMLElement).getAttribute('class') || '';
    // NB: truthy-проверка вместо `!== null` — встроенный DOM turndown'а
    // (Node-окружение, тесты) возвращает undefined при отсутствии совпадения.
    return cls.includes('wiki-task-item') || !!(node as HTMLElement).querySelector('input[type="checkbox"]');
  },
  replacement: (content, node) => {
    const checkbox = (node as HTMLElement).querySelector('input[type="checkbox"]');
    const li = node as HTMLElement;
    const checked =
      checkbox?.hasAttribute('checked') ||
      (checkbox as HTMLInputElement)?.checked ||
      li.getAttribute('data-checked') === 'true';
    const cleanContent = content.replace(/^\s*\[\s*[\sxX]?\s*\]\s*/, '').trim();
    return `- [${checked ? 'x' : ' '}] ${cleanContent}\n`;
  },
});

// Underline → __text__ (нестандартный markdown, но обратно парсится)
turndown.addRule('underline', {
  filter: ['u'],
  replacement: (content) => `__${content}__`,
});

// Highlight → ==text== (Obsidian-style)
turndown.addRule('highlight', {
  filter: ['mark'],
  replacement: (content) => `==${content}==`,
});

// Зачёркнутый → ~~text~~ (GFM). В ядре turndown правила для <s>/<del> нет —
// без этого правила зачёркивание ТЕРЯЛОСЬ при сохранении (текст оставался,
// форматирование пропадало).
turndown.addRule('strikethrough', {
  filter: (node) => ['DEL', 'S', 'STRIKE'].includes(node.nodeName),
  replacement: (content) => `~~${content}~~`,
});

// Callout «Предупреждение» → GitHub-admonition blockquote `> [!X]`.
// <div data-callout="info|warning|danger">…</div> сериализуется в:
//   > [!INFO]
//   > строка текста
// Деградирует в обычный blockquote, если где-то не распознано.
turndown.addRule('callout', {
  filter: (node) =>
    node.nodeName === 'DIV' && (node as HTMLElement).getAttribute('data-callout') !== null,
  replacement: (content, node) => {
    const sev = ((node as HTMLElement).getAttribute('data-callout') || 'info').toUpperCase();
    const tag = ['INFO', 'SUCCESS', 'DANGER', 'ATTENTION', 'WARNING', 'TIP', 'IDEA'].includes(sev)
      ? sev
      : 'INFO';
    // content уже отрендерен turndown'ом (markdown внутренних блоков).
    // Префиксуем каждую непустую строку `> `, как обычный blockquote.
    const inner = content.trim();
    const body = inner
      .split('\n')
      .map((line) => (line.length ? `> ${line}` : '>'))
      .join('\n');
    return `\n\n> [!${tag}]\n${body}\n\n`;
  },
});

// Галерея: <div data-gallery data-cols="N"><img…></div> → fenced-блок
//   ```gallery N
//   <url>
//   <url>
//   ```
// markdown-it (installGalleryFence) парсит обратно в div → Tiptap узнаёт ноду.
// Та же логика на бэке (wiki-markdown.service) для читалки. Round-trip целый.
turndown.addRule('imageGallery', {
  filter: (node) =>
    node.nodeName === 'DIV' && (node as HTMLElement).getAttribute('data-gallery') !== null,
  replacement: (_content, node) => {
    const el = node as HTMLElement;
    const cols = parseInt(el.getAttribute('data-cols') || '3', 10) || 3;
    const urls = Array.from(el.querySelectorAll('img'))
      .map((i) => i.getAttribute('src') || '')
      .filter(Boolean);
    if (urls.length === 0) return '';
    return `\n\n\`\`\`gallery ${cols}\n${urls.join('\n')}\n\`\`\`\n\n`;
  },
});

// Интерактивная таблица: <div data-fk-table='{json}'> → fenced-блок
//   ```fk-table
//   {"columns":[...],"rows":[...],"view":{...}}
//   ```
// markdown-it (installFkTableFence) парсит обратно в div. Синхронно с бэком.
turndown.addRule('fkTable', {
  filter: (node) =>
    node.nodeName === 'DIV' && (node as HTMLElement).getAttribute('data-fk-table') !== null,
  replacement: (_content, node) => {
    const raw = (node as HTMLElement).getAttribute('data-fk-table') || '';
    if (!raw) return '';
    let json = raw;
    try {
      json = JSON.stringify(JSON.parse(raw));
    } catch {
      /* оставляем как есть */
    }
    return `\n\n\`\`\`fk-table\n${json}\n\`\`\`\n\n`;
  },
});

// Видео с нашего S3: <figure data-video="id" data-caption="…"> → fenced-блок
//   ```video
//   {"id":"<attachmentId>","caption":"…"}
//   ```
// markdown-it (installVideoFence) парсит обратно в figure. Синхронно с бэком.
turndown.addRule('fkVideo', {
  filter: (node) =>
    node.nodeName === 'FIGURE' && (node as HTMLElement).getAttribute('data-video') !== null,
  replacement: (_content, node) => {
    const el = node as HTMLElement;
    const id = el.getAttribute('data-video') || '';
    if (!/^[0-9a-fA-F-]{8,64}$/.test(id)) return '';
    const caption = el.getAttribute('data-caption') || el.querySelector('figcaption')?.textContent || '';
    return `\n\n\`\`\`video\n${JSON.stringify({ id, caption })}\n\`\`\`\n\n`;
  },
});

// Колонки: <div data-columns><div class="fk-col">…</div>…</div> → ```columns N
// с разделителем ---col---; содержимое колонок — вложенный markdown.
// Если внутри уже есть ``` — берём более длинный забор ```` (по CommonMark).
function fenceFor(inner: string): string {
  return inner.includes('```') ? '````' : '```';
}
/** Прямые дети с классом (turndown работает на своём DOM без `:scope`). */
function childrenWithClass(el: HTMLElement, cls: string): HTMLElement[] {
  return Array.from(el.children).filter((c): c is HTMLElement => c.classList.contains(cls));
}
function childByTag(el: HTMLElement, tag: string): HTMLElement | null {
  return (Array.from(el.children).find((c) => c.nodeName === tag.toUpperCase()) as HTMLElement | undefined) ?? null;
}
turndown.addRule('fkColumns', {
  filter: (node) =>
    node.nodeName === 'DIV' && (node as HTMLElement).getAttribute('data-columns') !== null,
  replacement: (_content, node) => {
    const cols = childrenWithClass(node as HTMLElement, 'fk-col');
    if (cols.length === 0) return '';
    const parts = cols.map((c) => turndown.turndown(c.innerHTML).trim());
    const inner = parts.join('\n\n---col---\n\n');
    const f = fenceFor(inner);
    return `\n\n${f}columns ${cols.length}\n${inner}\n${f}\n\n`;
  },
});

// Раскрывающийся блок: <details data-toggle><summary>T</summary><div class="fk-toggle-body">…</div></details>
// → ```toggle T
turndown.addRule('fkToggle', {
  filter: (node) =>
    node.nodeName === 'DETAILS' && (node as HTMLElement).getAttribute('data-toggle') !== null,
  replacement: (_content, node) => {
    const el = node as HTMLElement;
    const title = (childByTag(el, 'summary')?.textContent || 'Заголовок').trim().replace(/\n/g, ' ');
    const body = childrenWithClass(el, 'fk-toggle-body')[0] ?? null;
    const inner = turndown.turndown(body ? body.innerHTML : '').trim();
    const f = fenceFor(inner);
    return `\n\n${f}toggle ${title}\n${inner}\n${f}\n\n`;
  },
});

// Шаги: <ol data-steps><li class="fk-step"><div class="fk-step-title">T</div><div class="fk-step-body">…</div></li></ol>
// → ```steps с секциями `### T`
turndown.addRule('fkSteps', {
  filter: (node) =>
    node.nodeName === 'OL' && (node as HTMLElement).getAttribute('data-steps') !== null,
  replacement: (_content, node) => {
    const steps = childrenWithClass(node as HTMLElement, 'fk-step');
    if (steps.length === 0) return '';
    const parts = steps.map((li) => {
      const title = (childrenWithClass(li, 'fk-step-title')[0]?.textContent || '').trim().replace(/\n/g, ' ');
      const body = childrenWithClass(li, 'fk-step-body')[0] ?? null;
      const inner = turndown.turndown(body ? body.innerHTML : '').trim();
      return `### ${title || 'Шаг'}${inner ? '\n' + inner : ''}`;
    });
    const inner = parts.join('\n\n');
    const f = fenceFor(inner);
    return `\n\n${f}steps\n${inner}\n${f}\n\n`;
  },
});

// Блоки фазы 1.7: embed / tabs / button / file / kpi (см. misc-nodes.tsx).
turndown.addRule('fkEmbed', {
  filter: (node) => node.nodeName === 'DIV' && (node as HTMLElement).getAttribute('data-embed') !== null,
  replacement: (_c, node) => {
    const el = node as HTMLElement;
    const url = el.getAttribute('data-embed') || '';
    if (!/^https:\/\//.test(url)) return '';
    const height = parseInt(el.getAttribute('data-height') || '480', 10) || 480;
    return `\n\n\`\`\`embed\n${JSON.stringify({ url, height })}\n\`\`\`\n\n`;
  },
});
turndown.addRule('fkTabs', {
  filter: (node) => node.nodeName === 'DIV' && (node as HTMLElement).getAttribute('data-tabs') !== null,
  replacement: (_c, node) => {
    const tabs = childrenWithClass(node as HTMLElement, 'fk-tab');
    if (tabs.length === 0) return '';
    const parts = tabs.map((t) => {
      const title = (t.getAttribute('data-title') || childrenWithClass(t, 'fk-tab-title')[0]?.textContent || 'Вкладка').trim().replace(/\n/g, ' ');
      const body = childrenWithClass(t, 'fk-tab-body')[0];
      const inner = turndown.turndown(body ? body.innerHTML : '').trim();
      return `## ${title}${inner ? '\n' + inner : ''}`;
    });
    const inner = parts.join('\n\n');
    const f = fenceFor(inner);
    return `\n\n${f}tabs\n${inner}\n${f}\n\n`;
  },
});
turndown.addRule('fkButton', {
  filter: (node) => node.nodeName === 'A' && (node as HTMLElement).getAttribute('data-button') !== null,
  replacement: (_c, node) => {
    const el = node as HTMLElement;
    const label = (el.textContent || 'Открыть').trim();
    const href = el.getAttribute('href') || '';
    const style = el.classList.contains('fk-button-default') ? 'default' : 'primary';
    return `\n\n\`\`\`button\n${JSON.stringify({ label, href, style })}\n\`\`\`\n\n`;
  },
});
turndown.addRule('fkFile', {
  filter: (node) => node.nodeName === 'A' && (node as HTMLElement).getAttribute('data-file') !== null,
  replacement: (_c, node) => {
    const el = node as HTMLElement;
    const url = el.getAttribute('href') || '';
    const name = el.getAttribute('data-name') || 'Файл';
    const size = parseInt(el.getAttribute('data-size') || '0', 10) || 0;
    if (!url) return '';
    return `\n\n\`\`\`file\n${JSON.stringify({ url, name, size })}\n\`\`\`\n\n`;
  },
});
turndown.addRule('fkKpi', {
  filter: (node) => node.nodeName === 'DIV' && (node as HTMLElement).getAttribute('data-kpi') !== null,
  replacement: (_c, node) => {
    const code = (node as HTMLElement).getAttribute('data-kpi') || '';
    if (!code) return '';
    return `\n\n\`\`\`kpi\n${JSON.stringify({ code })}\n\`\`\`\n\n`;
  },
});
// «Проверь себя» (фаза 2.1, уроки): <div class="fk-check" data-check data-correct data-explanation>
//   <div class="fk-check-q">вопрос</div><ol class="fk-check-options"><li>…</li></ol></div>
turndown.addRule('fkCheck', {
  filter: (node) => node.nodeName === 'DIV' && (node as HTMLElement).getAttribute('data-check') !== null,
  replacement: (_c, node) => {
    const el = node as HTMLElement;
    const q = (childrenWithClass(el, 'fk-check-q')[0]?.textContent || '').trim();
    const list = childrenWithClass(el, 'fk-check-options')[0];
    const options = list ? Array.from(list.children).map((li) => (li.textContent || '').trim()).filter(Boolean) : [];
    if (!q || options.length < 2) return '';
    const correct = parseInt(el.getAttribute('data-correct') || '0', 10) || 0;
    const explanation = el.getAttribute('data-explanation') || '';
    return `\n\n\`\`\`check\n${JSON.stringify({ q, options, correct, explanation })}\n\`\`\`\n\n`;
  },
});

// Картинка с подписью: <figure class="fk-figure"><img><figcaption>…</figcaption></figure>
// → ![alt](src "подпись") — стандартный title Markdown.
turndown.addRule('fkFigure', {
  filter: (node) =>
    node.nodeName === 'FIGURE' && (node as HTMLElement).classList.contains('fk-figure'),
  replacement: (_content, node) => {
    const el = node as HTMLElement;
    const img = el.querySelector('img');
    if (!img) return '';
    const src = img.getAttribute('src') || '';
    const alt = (img.getAttribute('alt') || '').replace(/[\[\]]/g, '');
    const caption = (el.querySelector('figcaption')?.textContent || '').trim().replace(/"/g, '\\"');
    return caption ? `\n\n![${alt}](${src} "${caption}")\n\n` : `\n\n![${alt}](${src})\n\n`;
  },
});

// Цитата с автором: <cite> внутри blockquote → последняя строка `— Автор`.
turndown.addRule('quoteCite', {
  filter: (node) => node.nodeName === 'CITE' && node.parentElement?.nodeName === 'BLOCKQUOTE',
  replacement: (content) => `\n\n— ${content.trim()}\n\n`,
});

// Таблицы — GFM
turndown.addRule('table', {
  filter: 'table',
  replacement: (_content, node) => {
    return convertTableToGfm(node as HTMLTableElement);
  },
});

export function htmlToMarkdown(html: string): string {
  return turndown.turndown(html).trim() + '\n';
}

/**
 * Регистрирует core-правило markdown-it: GitHub-admonition blockquote
 * `> [!INFO|WARNING|DANGER]` превращается в callout-div
 * `<div class="fk-callout fk-callout-X" data-callout="X">…</div>`.
 *
 * Это даёт круг: turndown пишет `> [!X]` → markdown-it парсит обратно в div
 * → Tiptap (parseHTML `div[data-callout]`) узнаёт ноду. Тот же рендер
 * используется на бэке для цветного блока в читалке.
 *
 * Обычный blockquote (без маркера `[!X]`) НЕ затрагивается.
 *
 * ВАЖНО: эта же логика продублирована в
 * backend/src/modules/wiki/wiki-markdown.service.ts — держать в синхроне.
 */
export function installCalloutRule(mdInstance: MarkdownIt): void {
  const MARKER = /^\[!(INFO|SUCCESS|DANGER|ATTENTION|WARNING|TIP|IDEA)\]\s*/i;

  mdInstance.core.ruler.after('block', 'fk_callout', (state) => {
    const tokens = state.tokens;
    for (let i = 0; i < tokens.length; i++) {
      if (tokens[i].type !== 'blockquote_open') continue;
      // Первый значимый токен внутри blockquote — paragraph_open, затем inline.
      const inlineTok = tokens[i + 2];
      if (
        !inlineTok ||
        tokens[i + 1]?.type !== 'paragraph_open' ||
        inlineTok.type !== 'inline'
      ) {
        continue;
      }
      const m = MARKER.exec(inlineTok.content);
      if (!m) continue;

      const severity = m[1].toLowerCase(); // info | warning | danger

      // Снимаем маркер `[!X]` из inline-контента (вместе с возможным
      // переводом строки сразу после него).
      inlineTok.content = inlineTok.content.replace(MARKER, '').replace(/^\n/, '');
      if (inlineTok.children && inlineTok.children.length) {
        const first = inlineTok.children[0];
        if (first.type === 'text') {
          first.content = first.content.replace(MARKER, '');
          // Если после снятия маркера осталась пустая первая строка —
          // убираем ведущий softbreak/hardbreak.
          if (first.content === '' && inlineTok.children[1]) {
            const second = inlineTok.children[1];
            if (second.type === 'softbreak' || second.type === 'hardbreak') {
              inlineTok.children.splice(0, 2);
            } else {
              inlineTok.children.splice(0, 1);
            }
          }
        }
      }

      // Если после снятия маркера параграф полностью пуст — убираем
      // пустой paragraph_open/inline/paragraph_close, чтобы не было
      // лишнего <p></p>.
      if (inlineTok.content.trim() === '' && (!inlineTok.children || inlineTok.children.length === 0)) {
        tokens.splice(i + 1, 3); // paragraph_open, inline, paragraph_close
      }

      // Подменяем blockquote_open / blockquote_close на html_block с div'ами.
      tokens[i].type = 'html_block';
      tokens[i].tag = '';
      tokens[i].block = true;
      tokens[i].content = `<div class="fk-callout fk-callout-${severity}" data-callout="${severity}">\n`;

      // Ищем парный blockquote_close (учитываем вложенность).
      let depth = 1;
      let j = i + 1;
      for (; j < tokens.length; j++) {
        if (tokens[j].type === 'blockquote_open') depth++;
        else if (tokens[j].type === 'blockquote_close') {
          depth--;
          if (depth === 0) break;
        }
      }
      if (j < tokens.length) {
        tokens[j].type = 'html_block';
        tokens[j].tag = '';
        tokens[j].block = true;
        tokens[j].content = `</div>\n`;
      }
    }
    return true;
  });
}

/**
 * Рендер fenced-блока ```gallery [N] в grid из <img> (data-gallery).
 * Переопределяет renderer.rules.fence только для info='gallery', остальные
 * code-fence не трогает. Работает при html:false (html_block обходится через
 * собственный renderer). Синхронно с backend wiki-markdown.service.ts.
 */
export function installGalleryFence(mdInstance: MarkdownIt): void {
  const esc = mdInstance.utils.escapeHtml;
  const prev = mdInstance.renderer.rules.fence;
  mdInstance.renderer.rules.fence = (tokens, idx, options, env, self) => {
    const token = tokens[idx];
    const info = (token.info || '').trim();
    if (info.split(/\s+/)[0] === 'gallery') {
      const colsRaw = parseInt(info.split(/\s+/)[1] || '3', 10) || 3;
      const cols = Math.min(5, Math.max(2, colsRaw));
      const imgs = token.content
        .split('\n')
        .map((s) => s.trim())
        .filter(Boolean)
        .map((u) => `<img src="${esc(u)}" loading="lazy" alt="" />`)
        .join('');
      return `<div class="fk-gallery" data-gallery data-cols="${cols}" style="--fk-gal-cols:${cols}">${imgs}</div>\n`;
    }
    return prev
      ? prev(tokens, idx, options, env, self)
      : self.renderToken(tokens, idx, options);
  };
}

/**
 * Рендер fenced-блока ```fk-table {json} в <div data-fk-table='{json}'> со
 * статической таблицей внутри (fallback до гидрации). На фронте читалки
 * (ArticlePage) этот div «оживляется» в интерактивный React-компонент.
 * Синхронно с backend wiki-markdown.service.ts.
 */
export function installFkTableFence(mdInstance: MarkdownIt): void {
  const esc = mdInstance.utils.escapeHtml;
  const prev = mdInstance.renderer.rules.fence;
  mdInstance.renderer.rules.fence = (tokens, idx, options, env, self) => {
    const token = tokens[idx];
    const info = (token.info || '').trim();
    if (info.split(/\s+/)[0] === 'fk-table') {
      const raw = token.content.trim();
      let data: { columns?: { id: string; name: string; hidden?: boolean }[]; rows?: { cells?: Record<string, string> }[] } = {};
      try {
        data = JSON.parse(raw);
      } catch {
        data = {};
      }
      const cols = (data.columns || []).filter((c) => !c.hidden);
      const head = cols.map((c) => `<th>${esc(c.name || '')}</th>`).join('');
      const body = (data.rows || [])
        .map(
          (r) =>
            `<tr>${cols.map((c) => `<td>${esc((r.cells?.[c.id] ?? '').toString())}</td>`).join('')}</tr>`,
        )
        .join('');
      const json = esc(raw);
      return `<div class="fk-table-block" data-fk-table="${json}"><table class="fk-table-static"><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table></div>\n`;
    }
    return prev
      ? prev(tokens, idx, options, env, self)
      : self.renderToken(tokens, idx, options);
  };
}

/**
 * Рендер fenced-блока ```video {"id","caption"} в <figure data-video> с
 * нативным <video> (src/poster — стабильные роуты /stream и /poster, бэк
 * редиректит на presigned S3). В читалке ArticlePage гидрирует figure в
 * FkVideoPlayer. Синхронно с backend wiki-markdown.service.ts.
 */
export function installVideoFence(mdInstance: MarkdownIt): void {
  const esc = mdInstance.utils.escapeHtml;
  const prev = mdInstance.renderer.rules.fence;
  mdInstance.renderer.rules.fence = (tokens, idx, options, env, self) => {
    const token = tokens[idx];
    const info = (token.info || '').trim();
    if (info.split(/\s+/)[0] === 'video') {
      let data: { id?: unknown; caption?: unknown } = {};
      try {
        data = JSON.parse(token.content.trim());
      } catch {
        data = {};
      }
      const id = String(data.id ?? '');
      if (!/^[0-9a-fA-F-]{8,64}$/.test(id)) return '';
      const caption = esc(String(data.caption ?? '').slice(0, 300));
      return (
        `<figure class="fk-video" data-video="${id}" data-caption="${caption}">` +
        `<video controls preload="metadata" playsinline poster="/api/wiki/attachments/${id}/poster" src="/api/wiki/attachments/${id}/stream"></video>` +
        (caption ? `<figcaption>${caption}</figcaption>` : '') +
        `</figure>\n`
      );
    }
    return prev
      ? prev(tokens, idx, options, env, self)
      : self.renderToken(tokens, idx, options);
  };
}

/**
 * Fenced-блоки компоновки (фаза 0.3): ```columns N (---col---), ```toggle T,
 * ```steps (### секции). Содержимое — вложенный Markdown, рендерим тем же
 * markdown-it рекурсивно. Синхронно с backend wiki-markdown.service.ts.
 */
export function installLayoutFences(mdInstance: MarkdownIt): void {
  const esc = mdInstance.utils.escapeHtml;
  const prev = mdInstance.renderer.rules.fence;
  mdInstance.renderer.rules.fence = (tokens, idx, options, env, self) => {
    const token = tokens[idx];
    const info = (token.info || '').trim();
    const kind = info.split(/\s+/)[0];
    if (kind === 'columns') {
      const segs = token.content.split(/\n---col---\n?/).map((s) => s.trim());
      const cols = segs.slice(0, 4);
      const inner = cols.map((c) => `<div class="fk-col">${mdInstance.render(c, env)}</div>`).join('');
      return `<div class="fk-columns" data-columns="${cols.length}">${inner}</div>\n`;
    }
    if (kind === 'toggle') {
      const title = info.slice('toggle'.length).trim() || 'Заголовок';
      return `<details class="fk-toggle" data-toggle><summary>${esc(title)}</summary><div class="fk-toggle-body">${mdInstance.render(token.content, env)}</div></details>\n`;
    }
    if (kind === 'steps') {
      const parts = token.content.split(/^###\s+/m).map((s) => s.replace(/\s+$/, ''));
      const steps = parts.filter((p, i) => i > 0 || p.trim()).map((p, i, arr) => {
        // Первый сегмент без «### » — текст до первого шага: показываем как шаг без названия.
        const isLead = i === 0 && arr.length === parts.length && !token.content.startsWith('### ');
        const nl = p.indexOf('\n');
        const title = isLead ? '' : (nl === -1 ? p : p.slice(0, nl)).trim();
        const body = isLead ? p : nl === -1 ? '' : p.slice(nl + 1);
        return `<li class="fk-step"><div class="fk-step-title">${esc(title)}</div><div class="fk-step-body">${mdInstance.render(body.trim(), env)}</div></li>`;
      });
      return `<ol class="fk-steps" data-steps>${steps.join('')}</ol>\n`;
    }
    return prev
      ? prev(tokens, idx, options, env, self)
      : self.renderToken(tokens, idx, options);
  };
}

/**
 * Fenced-блоки фазы 1.7. iframe для embed НЕ рендерится — только
 * <div data-embed> со ссылкой; читалка строит iframe по allow-list (lib/embed).
 * Синхронно с backend wiki-markdown.service.ts.
 */
export function installMiscFences(mdInstance: MarkdownIt): void {
  const esc = mdInstance.utils.escapeHtml;
  const prev = mdInstance.renderer.rules.fence;
  const safeHref = (h: string): string => (/^(https?:\/\/|\/)/.test(h) ? h : '#');
  mdInstance.renderer.rules.fence = (tokens, idx, options, env, self) => {
    const token = tokens[idx];
    const info = (token.info || '').trim();
    const kind = info.split(/\s+/)[0];
    const json = (): Record<string, unknown> => {
      try {
        const v = JSON.parse(token.content.trim());
        return v && typeof v === 'object' ? (v as Record<string, unknown>) : {};
      } catch {
        return {};
      }
    };
    if (kind === 'embed') {
      const d = json();
      const url = String(d.url ?? '');
      if (!/^https:\/\//.test(url)) return '';
      const height = Math.min(1200, Math.max(200, Number(d.height) || 480));
      return `<div class="fk-embed" data-embed="${esc(url)}" data-height="${height}"><a href="${esc(url)}" target="_blank" rel="noopener noreferrer">${esc(url)}</a></div>\n`;
    }
    if (kind === 'tabs') {
      const parts = token.content.split(/^##\s+/m).map((s) => s.replace(/\s+$/, ''));
      const tabs = parts.filter((p, i) => i > 0 || p.trim()).map((p, i, arr) => {
        const isLead = i === 0 && arr.length === parts.length && !token.content.startsWith('## ');
        const nl = p.indexOf('\n');
        const title = isLead ? 'Вкладка' : (nl === -1 ? p : p.slice(0, nl)).trim() || 'Вкладка';
        const body = isLead ? p : nl === -1 ? '' : p.slice(nl + 1);
        return `<div class="fk-tab" data-title="${esc(title)}"><div class="fk-tab-title">${esc(title)}</div><div class="fk-tab-body">${mdInstance.render(body.trim(), env)}</div></div>`;
      });
      return `<div class="fk-tabs" data-tabs>${tabs.join('')}</div>\n`;
    }
    if (kind === 'button') {
      const d = json();
      const label = String(d.label ?? 'Открыть').slice(0, 120);
      const href = safeHref(String(d.href ?? ''));
      const style = d.style === 'default' ? 'default' : 'primary';
      return `<a class="fk-button fk-button-${style}" data-button href="${esc(href)}" rel="noopener">${esc(label)}</a>\n`;
    }
    if (kind === 'file') {
      const d = json();
      const url = safeHref(String(d.url ?? ''));
      if (url === '#') return '';
      const name = String(d.name ?? 'Файл').slice(0, 200);
      const size = Math.max(0, Number(d.size) || 0);
      return `<a class="fk-file" data-file data-name="${esc(name)}" data-size="${size}" href="${esc(url)}" target="_blank" rel="noopener"><span class="fk-file-icon">${fileIconFor(name)}</span><span class="fk-file-name">${esc(name)}</span><span class="fk-file-size">${formatSizeFor(size)}</span></a>\n`;
    }
    if (kind === 'kpi') {
      const d = json();
      const code = String(d.code ?? '').replace(/[^\w.-]/g, '').slice(0, 60);
      if (!code) return '';
      return `<div class="fk-kpi" data-kpi="${esc(code)}"><span class="fk-kpi-code">KPI: ${esc(code)}</span></div>\n`;
    }
    if (kind === 'check') {
      // «Проверь себя» (фаза 2.1): ответ проверяется на клиенте, в зачёт не идёт.
      const d = json();
      const q = String(d.q ?? '').slice(0, 500);
      const options = Array.isArray(d.options) ? d.options.map((o) => String(o).slice(0, 300)).filter(Boolean).slice(0, 8) : [];
      if (!q || options.length < 2) return '';
      const correct = Math.max(0, Math.min(options.length - 1, Number(d.correct) || 0));
      const explanation = String(d.explanation ?? '').slice(0, 1000);
      return `<div class="fk-check" data-check data-correct="${correct}" data-explanation="${esc(explanation)}"><div class="fk-check-q">${esc(q)}</div><ol class="fk-check-options">${options.map((o) => `<li class="fk-check-option">${esc(o)}</li>`).join('')}</ol></div>\n`;
    }
    return prev ? prev(tokens, idx, options, env, self) : self.renderToken(tokens, idx, options);
  };
}

function fileIconFor(name: string): string {
  const ext = (name.split('.').pop() || '').toLowerCase();
  if (ext === 'pdf') return '📕';
  if (['doc', 'docx', 'rtf'].includes(ext)) return '📘';
  if (['xls', 'xlsx', 'csv'].includes(ext)) return '📗';
  if (['ppt', 'pptx'].includes(ext)) return '📙';
  if (['zip', 'rar', '7z'].includes(ext)) return '🗜️';
  return '📄';
}

function formatSizeFor(bytes: number): string {
  if (!bytes || bytes <= 0) return '';
  if (bytes < 1024) return `${bytes} Б`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} КБ`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} МБ`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} ГБ`;
}

/**
 * Картинка с подписью: ![alt](url "Подпись") → <figure class="fk-figure">.
 * Без title — обычный <img> (как раньше). Абзац вокруг снимает postProcessHtml.
 */
export function installFigureRule(mdInstance: MarkdownIt): void {
  const esc = mdInstance.utils.escapeHtml;
  const prev = mdInstance.renderer.rules.image;
  mdInstance.renderer.rules.image = (tokens, idx, options, env, self) => {
    const token = tokens[idx];
    const title = token.attrGet('title');
    if (!title) {
      return prev ? prev(tokens, idx, options, env, self) : self.renderToken(tokens, idx, options);
    }
    const src = esc(token.attrGet('src') || '');
    const alt = esc(token.content || '');
    return `<figure class="fk-figure"><img src="${src}" alt="${alt}" class="wiki-embed" loading="lazy" /><figcaption>${esc(title)}</figcaption></figure>`;
  };
}

/**
 * Inline-rule для [[wikilinks]] — порт правила из
 * backend/src/modules/wiki/wiki-markdown.service.ts (держать в синхроне).
 *
 * ВАЖНО: раньше wikilinks подставлялись regex-препроцессингом сырого HTML
 * в markdown-строку, но md создан с html:false — markdown-it ЭКРАНИРОВАЛ
 * вставленный <a> как текст, а typographer ещё и менял кавычки атрибутов
 * на «ёлочки». В итоге wikilink в редакторе превращался в мусорный текст
 * и безвозвратно ломался при сохранении. Inline-rule пушит html_inline
 * токен — он рендерится как есть независимо от опции html.
 *
 * `![[embed]]` (картинки из vault) намеренно НЕ трогаем — оставляем
 * литеральным текстом, как и раньше (редактор их не поддерживает).
 */
export function installWikilinkRule(mdInstance: MarkdownIt): void {
  const esc = mdInstance.utils.escapeHtml;
  mdInstance.inline.ruler.before('link', 'fk_wikilink', (state, silent) => {
    const src = state.src;
    if (src.charCodeAt(state.pos) !== 0x5b /* [ */) return false;
    if (src.charCodeAt(state.pos + 1) !== 0x5b /* [ */) return false;
    // ![[image]] — embed, не ссылка: пропускаем (останется текстом).
    if (state.pos > 0 && src.charCodeAt(state.pos - 1) === 0x21 /* ! */) return false;
    const end = src.indexOf(']]', state.pos + 2);
    if (end === -1) return false;
    const inner = src.slice(state.pos + 2, end);
    if (inner.includes('\n') || inner.trim() === '') return false;
    if (!silent) {
      const pipe = inner.indexOf('|');
      const slug = (pipe === -1 ? inner : inner.slice(0, pipe)).trim();
      const label = (pipe === -1 ? inner : inner.slice(pipe + 1)).trim();
      const token = state.push('html_inline', '', 0);
      token.content = `<a href="/article/${esc(slug)}" data-wikilink="${esc(slug)}" class="wiki-link">${esc(label || slug)}</a>`;
    }
    state.pos = end + 2;
    return true;
  });
}

/**
 * Внутренняя ссылка на файл вики: диск (`/api/wiki/disk/files/<id>/download`)
 * или вложение статьи (`/api/wiki/attachments/<id>/…`). Только такие пути — внешние
 * URL и прочие `/api/…` не трогаем.
 */
export function isInternalWikiFileUrl(url: string): boolean {
  return /^\/api\/wiki\/(?:disk\/files|attachments)\/[\w\-./%?=&~]+$/.test(url) && !url.includes('..');
}

/**
 * Inline-rule: экранированная ссылка на внутренний файл вики
 * `\[имя\](/api/wiki/disk/files/<id>/download)` → обычная ссылка (владелец 22.10.2026).
 *
 * Откуда берётся: кнопка «В статью» на Диске копировала markdown-ссылку, её вставляли в
 * редактор как ТЕКСТ, а при сохранении turndown экранировал скобки — в читалке оставался
 * текст со скобками вместо ссылки. Правило чинит уже сохранённые статьи без правки данных:
 * при следующем сохранении в markdown уйдёт нормальная `[имя](url)`.
 *
 * Именно inline-rule, а не замена по regex в исходнике: код (``` и `…`) разбирается раньше
 * и сюда не попадает, а вложенные блоки (колонки, вкладки, шаги) рендерятся тем же md.
 * Синхронно с backend wiki-markdown.service.ts (installEscapedFileLinkRule).
 */
export function installEscapedFileLinkRule(mdInstance: MarkdownIt): void {
  mdInstance.inline.ruler.before('escape', 'fk_escaped_file_link', (state, silent) => {
    const src = state.src;
    const start = state.pos;
    if (src.charCodeAt(start) !== 0x5c /* \ */ || src.charCodeAt(start + 1) !== 0x5b /* [ */) return false;
    // Подпись — до `\]`, одной строкой и без вложенных `\[`.
    let labelEnd = -1;
    for (let i = start + 2; i < state.posMax; ) {
      const ch = src.charCodeAt(i);
      if (ch === 0x0a) return false;
      if (ch === 0x5c) {
        const next = src.charCodeAt(i + 1);
        if (next === 0x5d /* ] */) {
          labelEnd = i;
          break;
        }
        if (next === 0x5b /* [ */) return false;
        i += 2;
        continue;
      }
      i += 1;
    }
    if (labelEnd < 0 || labelEnd === start + 2) return false;
    // Сразу за `\]` — `(url)`.
    let p = labelEnd + 2;
    if (src.charCodeAt(p) !== 0x28 /* ( */) return false;
    p += 1;
    const urlStart = p;
    while (p < state.posMax) {
      const ch = src.charCodeAt(p);
      if (ch === 0x29 /* ) */ || ch <= 0x20) break;
      p += 1;
    }
    if (src.charCodeAt(p) !== 0x29) return false;
    // turndown мог экранировать знаки в самом пути (`\_`) — снимаем обратные слэши.
    const url = src.slice(urlStart, p).replace(/\\([!-/:-@[-`{-~])/g, '$1');
    if (!isInternalWikiFileUrl(url) || !state.md.validateLink(url)) return false;

    if (!silent) {
      const open = state.push('link_open', 'a', 1);
      open.attrs = [['href', state.md.normalizeLink(url)]];
      const oldPos = state.pos;
      const oldMax = state.posMax;
      state.pos = start + 2;
      state.posMax = labelEnd;
      // linkLevel есть в markdown-it 14, но не в @types: внутри ссылки голый URL не должен становиться второй ссылкой.
      const levels = state as unknown as { linkLevel: number };
      levels.linkLevel += 1;
      state.md.inline.tokenize(state);
      levels.linkLevel -= 1;
      state.pos = oldPos;
      state.posMax = oldMax;
      state.push('link_close', 'a', -1);
    }
    state.pos = p + 1;
    return true;
  });
}

/**
 * Core-rule: GFM task-list `- [ ] текст` / `- [x] текст` → Tiptap-совместимый
 * HTML (ul[data-type="taskList"] > li[data-type="taskItem"][data-checked]
 * с <input type="checkbox"> внутри).
 *
 * Без этого правила круг ломал данные: turndown сериализует Tiptap-чеклист
 * в `- [ ] x`, а markdown-it (без task-плагина) рендерил это литеральным
 * текстом `[ ] x` внутри обычного <li>; при следующем сохранении turndown
 * экранировал скобки → `- \[ \] x` — синтаксис чек-листа безвозвратно
 * портился (в т.ч. ломался backend extractChecklistItems).
 */
export function installTaskListRule(mdInstance: MarkdownIt): void {
  const MARKER = /^\[( |x|X)\](?:\s+|$)/;
  mdInstance.core.ruler.after('block', 'fk_task_list', (state) => {
    const tokens = state.tokens;
    // Стек открытых bullet_list_open — чтобы пометить родительский список.
    const listStack: number[] = [];
    for (let i = 0; i < tokens.length; i++) {
      const t = tokens[i];
      if (t.type === 'bullet_list_open') listStack.push(i);
      else if (t.type === 'bullet_list_close') listStack.pop();
      if (t.type !== 'list_item_open') continue;
      // Tight-список: list_item_open → paragraph_open → inline.
      const inlineTok = tokens[i + 2];
      if (tokens[i + 1]?.type !== 'paragraph_open' || inlineTok?.type !== 'inline') continue;
      const m = MARKER.exec(inlineTok.content);
      if (!m) continue;

      const checked = m[1].toLowerCase() === 'x';

      // Снимаем маркер `[ ] ` из контента и первого text-child'а.
      inlineTok.content = inlineTok.content.replace(MARKER, '');
      const first = inlineTok.children?.[0];
      if (first && first.type === 'text') first.content = first.content.replace(MARKER, '');

      // Атрибуты li — Tiptap TaskItem парсит li[data-type="taskItem"].
      t.attrSet('data-type', 'taskItem');
      t.attrSet('data-checked', checked ? 'true' : 'false');
      t.attrSet('class', 'wiki-task-item');

      // Чекбокс в начало пункта: его видит и turndown (round-trip → `- [x]`),
      // и читалка (интерактивные чек-листы по input[type=checkbox]).
      const box = new state.Token('html_inline', '', 0);
      box.content = `<input type="checkbox"${checked ? ' checked="checked"' : ''} disabled /> `;
      inlineTok.children?.unshift(box);

      // Помечаем родительский список как taskList (для Tiptap TaskList).
      const listIdx = listStack[listStack.length - 1];
      if (listIdx != null) {
        const listTok = tokens[listIdx];
        if (listTok.attrGet('data-type') !== 'taskList') {
          listTok.attrSet('data-type', 'taskList');
          listTok.attrSet('class', 'wiki-task-list');
        }
      }
    }
    return true;
  });
}

function convertTableToGfm(table: HTMLTableElement): string {
  const rows = Array.from(table.querySelectorAll('tr'));
  if (rows.length === 0) return '';
  const cells = rows.map((tr) =>
    Array.from(tr.querySelectorAll('th, td')).map((c) =>
      (c.textContent || '').trim().replace(/\|/g, '\\|'),
    ),
  );
  const colCount = Math.max(...cells.map((r) => r.length));
  const pad = (r: string[]) => {
    const padded = [...r];
    while (padded.length < colCount) padded.push('');
    return padded;
  };
  const out: string[] = [];
  out.push('| ' + pad(cells[0]).join(' | ') + ' |');
  out.push('| ' + Array(colCount).fill('---').join(' | ') + ' |');
  for (let i = 1; i < cells.length; i++) {
    out.push('| ' + pad(cells[i]).join(' | ') + ' |');
  }
  return '\n' + out.join('\n') + '\n';
}
